import { Component, ViewEncapsulation, input, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { noop } from 'rxjs';
import { NgClass } from '@angular/common';

import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApContentService } from '@ap/content';

import content from './switch.content.json';

/**
 * @ngdoc component
 * @name ApSwitchComponent
 * @description A switch to toggle a choice. Works like the native checkbox input type but with different styling.
 **/
@Component({
    selector: 'ap-switch',
    templateUrl: './switch.component.html',
    styleUrls: ['./switch.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, ApFormErrorsComponent]
})
export class ApSwitchComponent implements ControlValueAccessor {
    protected control = inject(NgControl, { self: true, optional: true })!;
    protected readonly contentService = inject(ApContentService);

    /**
     * Aria-label. Should use this when there is no visual label
     */
    public readonly label = input<string>();

    /**
     * Error message object. Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages>(undefined);

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `switch-${Math.round(Math.random() * 10000000)}`;

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    constructor() {
        this.control && (this.control.valueAccessor = this);
        this.contentService.addContentItems(content);
        this.content = content;
    }

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    /** The switch is disabled but doesn't look like it. Useful when switch is only controlled through FormControl */
    protected readonly nonInteractive = input(false);

    private _isDisabled = false;

    /** @internal */
    public get isDisabled(): boolean {
        return this._isDisabled || this.nonInteractive();
    }

    /** @internal */
    public set isDisabled(disabled: boolean) {
        this._isDisabled = disabled;
    }

    /** @internal */
    public isChecked = false;

    /**
     * Set checked state if input changes
     */
    public switchChange(event: Event): void {
        this.isChecked = (event.target as HTMLInputElement).checked;
        this.onChange(this.isChecked);
        this.onTouched();
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function.
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(checked: boolean) {
        this.isChecked = checked;
    }

    /**
     * An HTMLLabelElement with an HTMLInputElement child will fire two different click events.
     * To prevent issues we only allow one.
     * @param {MouseEvent} e
     * @protected
     */
    protected onSliderClick(e: MouseEvent) {
        e.stopPropagation();
    }
}
